/* eslint-disable no-unused-vars */
import React, {
  useState,
  useEffect,
  useContext,
  useCallback,
  useRef,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  FileText,
  Search,
  Filter,
  Calendar,
  CalendarClock,
  User,
  AlertCircle,
  ChevronDown,
  Eye,
  Briefcase,
  MoreVertical,
  XCircle,
  CheckCircle,
  RefreshCw,
} from "lucide-react";
import { AdminContext } from "../../../context/AdminContext";
import { toast } from "react-toastify";
import Pagination from "../../../components/Pagination";

const ContractList = () => {
  const navigate = useNavigate();
  const {
    fetchContracts,
    contracts,
    contractsPagination,
    terminateContract,
    completeContract,
    updateContractStartDate,
  } = useContext(AdminContext);

  const [searchTerm, setSearchTerm] = useState("");
  const [searchInput, setSearchInput] = useState(""); // Separate input state
  const [statusFilter, setStatusFilter] = useState("All Statuses");
  const [showFilters, setShowFilters] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showTerminateModal, setShowTerminateModal] = useState(false);
  const [showStartDateModal, setShowStartDateModal] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);
  const [terminationReason, setTerminationReason] = useState("");
  const [startDateValue, setStartDateValue] = useState("");
  const [isUpdatingStartDate, setIsUpdatingStartDate] = useState(false);
  const [activeMenu, setActiveMenu] = useState(null);
  const [contractsLoading, setContractsLoading] = useState(false);
  const statusDropdownRef = useRef(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);

  const loadContracts = useCallback(async () => {
    try {
      setContractsLoading(true);
      await fetchContracts({
        page: currentPage,
        limit: itemsPerPage,
        status:
          statusFilter === "All Statuses"
            ? undefined
            : statusFilter.toUpperCase(),
        search: searchTerm,
      });
    } finally {
      setContractsLoading(false);
    }
  }, [fetchContracts, currentPage, itemsPerPage, statusFilter, searchTerm]);

  useEffect(() => {
    loadContracts();
  }, [loadContracts]);

  // Reset to page 1 when filters change (but not when page changes)
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, itemsPerPage]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (event.target.closest(".action-menu")) return;
      setActiveMenu(null);

      if (
        statusDropdownRef.current &&
        !statusDropdownRef.current.contains(event.target)
      ) {
        setShowStatusDropdown(false);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const getContractId = (contract) => contract.id;

  const getClient = (contract) => contract.client || contract.user || {};

  const getContractSource = (contract) => {
    if (contract.booking || contract.bookingId) {
      return {
        label: "Booking",
        detail: contract.booking?.status || "Booking contract",
        icon: Calendar,
      };
    }

    return {
      label: "Job Post",
      detail: contract.jobPost?.workState?.replace(/_/g, " ") || "Job contract",
      icon: Briefcase,
    };
  };

  const handleResetFilters = () => {
    setSearchInput("");
    setSearchTerm("");
    setStatusFilter("All Statuses");
    setCurrentPage(1);
  };

  // Display contracts from server (already paginated)
  const displayContracts = contracts || [];

  // Use server-side pagination data
  const totalPages = contractsPagination?.totalPages || 1;
  const totalItems = contractsPagination?.total || 0;

  const handleSearch = () => {
    setSearchTerm(searchInput);
    setCurrentPage(1);
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const handleStatusFilter = (status) => {
    setStatusFilter(status);
    setShowStatusDropdown(false);
  };

  const handleTerminate = async () => {
    if (!terminationReason.trim()) {
      toast.error("Please provide a termination reason");
      return;
    }

    await terminateContract(getContractId(selectedContract), terminationReason);
    await loadContracts();
    setShowTerminateModal(false);
    setSelectedContract(null);
    setTerminationReason("");
    setActiveMenu(null);
  };

  const handleComplete = async (contract) => {
    await completeContract(getContractId(contract));
    await loadContracts();
    setActiveMenu(null);
  };

  const formatDateForInput = (value) => {
    if (!value) return "";
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: "Africa/Addis_Ababa",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(new Date(value));
    const values = Object.fromEntries(
      parts
        .filter(({ type }) => type !== "literal")
        .map(({ type, value: partValue }) => [type, partValue]),
    );
    return `${values.year}-${values.month}-${values.day}`;
  };

  const openStartDateModal = (contract) => {
    setSelectedContract(contract);
    setStartDateValue(formatDateForInput(contract.startDate));
    setShowStartDateModal(true);
    setActiveMenu(null);
  };

  const handleStartDateUpdate = async () => {
    if (!startDateValue || !selectedContract) {
      toast.error("Please select a start date");
      return;
    }

    setIsUpdatingStartDate(true);
    const result = await updateContractStartDate(
      getContractId(selectedContract),
      startDateValue,
    );
    setIsUpdatingStartDate(false);

    if (result?.success) {
      await loadContracts();
      setShowStartDateModal(false);
      setSelectedContract(null);
      setStartDateValue("");
    }
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case "ACTIVE":
        return "bg-emerald-600 text-white border-emerald-700 dark:bg-emerald-700 dark:text-white dark:border-emerald-800";
      case "PAUSED":
        return "bg-yellow-600 text-white border-yellow-700 dark:bg-yellow-700 dark:text-white dark:border-yellow-800";
      case "TERMINATED":
        return "bg-red-600 text-white border-red-700 dark:bg-red-700 dark:text-white dark:border-red-800";
      case "COMPLETED":
        return "bg-gray-600 text-white border-gray-700 dark:bg-gray-700 dark:text-white dark:border-gray-800";
      default:
        return "bg-gray-600 text-white border-gray-700";
    }
  };

  return (
    <div className="animate-in fade-in slide-in-from-right-4 duration-500 pb-12">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-text dark:text-dark-text">
          Contracts
        </h1>
        <p className="text-gray-500 dark:text-dark-muted mt-1">
          Manage caregiver-client contracts and monthly payouts
        </p>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-dark-surface p-1.5 rounded-2xl shadow-sm border border-gray-100 dark:border-dark-border mb-6 flex gap-2">
        {/* Search Input */}
        <div className="relative flex-1">
          <Search
            className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
            size={20}
          />
          <input
            type="text"
            placeholder="Search by client or caregiver name..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyPress={handleSearchKeyPress}
            className="w-full pl-12 pr-4 py-3 rounded-xl bg-accent dark:bg-gray-900 border border-transparent focus:bg-card dark:focus:bg-dark-surface focus:border-primary outline-none text-sm transition-all text-text dark:text-dark-text placeholder-gray-400"
          />
        </div>

        {/* Search Button */}
        <button
          onClick={handleSearch}
          className="flex items-center justify-center px-4 py-3 bg-primary hover:bg-primary/90 text-white rounded-xl text-sm font-medium transition-colors"
        >
          <Search size={16} />
        </button>

        {/* Status Filter Dropdown */}
        <div className="relative" ref={statusDropdownRef}>
          <button
            onClick={() => setShowStatusDropdown(!showStatusDropdown)}
            className="flex items-center gap-2 px-4 py-3 bg-accent dark:bg-gray-900 text-gray-600 dark:text-dark-muted rounded-xl text-sm font-medium border border-transparent hover:border-gray-200 dark:hover:border-dark-border transition-colors whitespace-nowrap"
          >
            <Filter size={16} />
            {statusFilter}
            <ChevronDown
              size={14}
              className={`transition-transform ${
                showStatusDropdown ? "rotate-180" : ""
              }`}
            />
          </button>

          {showStatusDropdown && (
            <div className="absolute right-0 mt-2 w-48 bg-card dark:bg-dark-surface rounded-xl shadow-xl border border-gray-100 dark:border-dark-border p-1 z-50 animate-in fade-in zoom-in-95 duration-200">
              {[
                "All Statuses",
                "Active",
                "Paused",
                "Terminated",
                "Completed",
              ].map((status) => (
                <button
                  key={status}
                  onClick={() => handleStatusFilter(status)}
                  className={`w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    statusFilter === status
                      ? "bg-primary text-white dark:bg-primary"
                      : "text-gray-600 dark:text-dark-muted hover:bg-accent dark:hover:bg-gray-800"
                  }`}
                >
                  {status}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Reset Button */}
        <button
          onClick={handleResetFilters}
          className="flex items-center justify-center px-4 py-3 rounded-xl bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400 font-semibold hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors border border-gray-200 dark:border-gray-700"
          title="Reset Filters"
        >
          <RefreshCw size={18} />
        </button>
      </div>

      {/* Contracts Table */}
      <div className="bg-white dark:bg-dark-surface rounded-xl shadow-sm border border-gray-200 dark:border-dark-border overflow-visible">
        <div className="overflow-visible min-h-[400px]">
          <table className="w-full text-sm text-left">
            <thead className="text-xs text-gray-500 dark:text-dark-muted uppercase bg-gray-50 dark:bg-gray-900/50">
              <tr>
                <th className="px-6 py-4 font-bold tracking-wider">Client</th>
                <th className="px-6 py-4 font-bold tracking-wider">
                  Caregiver
                </th>
                <th className="px-6 py-4 font-bold tracking-wider">
                  Monthly Salary
                </th>
                <th className="px-6 py-4 font-bold tracking-wider">Source</th>
                <th className="px-6 py-4 font-bold tracking-wider">Status</th>
                <th className="px-6 py-4 font-bold tracking-wider">
                  Start Date
                </th>
                <th className="px-6 py-4 font-bold tracking-wider text-center">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {contractsLoading ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center">
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                    </div>
                  </td>
                </tr>
              ) : displayContracts.length > 0 ? (
                displayContracts.map((contract) => {
                  const source = getContractSource(contract);
                  const SourceIcon = source.icon;

                  return (
                    <tr
                      key={getContractId(contract)}
                      className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <User size={16} className="text-gray-400" />
                          <span className="font-medium text-text dark:text-dark-text">
                            {getClient(contract).name || "N/A"}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <User size={16} className="text-gray-400" />
                          <span className="font-medium text-text dark:text-dark-text">
                            {contract.caregiver?.name || "N/A"}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-text dark:text-dark-text">
                            ETB{" "}
                            {Number(contract.monthlySalary || 0).toLocaleString()}
                          </span>
                        </div>
                        {contract.status === "ACTIVE" &&
                          contract.paymentSummary?.hasUnpaidBill && (
                            <button
                              onClick={() =>
                                navigate(
                                  `/contracts/${getContractId(contract)}`,
                                )
                              }
                              className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-700 hover:bg-amber-100 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300 dark:hover:bg-amber-900/40"
                            >
                              <AlertCircle size={13} />
                              Open unpaid bill
                              <span>
                                ({contract.paymentSummary.unpaidCount})
                              </span>
                            </button>
                          )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <SourceIcon size={16} className="text-gray-400" />
                          <div>
                            <p className="font-medium text-text dark:text-dark-text">
                              {source.label}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-dark-muted capitalize">
                              {source.detail}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${getStatusStyle(
                            contract.status,
                          )}`}
                        >
                          {contract.status}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-1 text-gray-600 dark:text-dark-muted">
                          <Calendar size={14} className="text-gray-400" />
                          <span>
                            {contract.startDate
                              ? new Date(
                                  contract.startDate,
                                ).toLocaleDateString()
                              : "N/A"}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-center relative action-menu">
                        <div className="flex items-center justify-center">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setActiveMenu(
                                activeMenu === getContractId(contract)
                                  ? null
                                  : getContractId(contract),
                              );
                            }}
                            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg text-gray-500 dark:text-dark-muted hover:text-text dark:hover:text-dark-text transition-colors"
                            title="Contract actions"
                          >
                            <MoreVertical size={18} />
                          </button>

                          {activeMenu === getContractId(contract) && (
                            <div className="absolute right-4 top-full mt-2 w-48 bg-white dark:bg-dark-surface rounded-lg shadow-lg border border-gray-200 dark:border-dark-border z-50 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200 py-1">
                              <button
                                onClick={() => {
                                  navigate(
                                    `/contracts/${getContractId(contract)}`,
                                  );
                                  setActiveMenu(null);
                                }}
                                className="w-full text-left px-4 py-2.5 text-sm text-gray-700 dark:text-dark-text hover:bg-gray-50 dark:hover:bg-gray-800 flex items-center gap-2 transition-colors"
                              >
                                <Eye
                                  size={16}
                                  className="text-gray-500 dark:text-gray-400"
                                />
                                <span>View Details</span>
                              </button>

                              {["ACTIVE", "PAUSED"].includes(
                                contract.status,
                              ) && (
                                <>
                                  <div className="h-px bg-gray-100 dark:bg-gray-700 my-1"></div>
                                  <button
                                    onClick={() => openStartDateModal(contract)}
                                    className="w-full text-left px-4 py-2.5 text-sm text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 flex items-center gap-2 transition-colors"
                                  >
                                    <CalendarClock size={16} />
                                    <span>Adjust Start Date</span>
                                  </button>
                                  <button
                                    onClick={() => handleComplete(contract)}
                                    className="w-full text-left px-4 py-2.5 text-sm text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 flex items-center gap-2 transition-colors"
                                  >
                                    <CheckCircle size={16} />
                                    <span>Complete Contract</span>
                                  </button>
                                  <button
                                    onClick={() => {
                                      setSelectedContract(contract);
                                      setShowTerminateModal(true);
                                      setActiveMenu(null);
                                    }}
                                    className="w-full text-left px-4 py-2.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2 transition-colors"
                                  >
                                    <XCircle size={16} />
                                    <span>Terminate Contract</span>
                                  </button>
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td
                    colSpan={7}
                    className="px-6 py-12 text-center text-gray-500 dark:text-dark-muted"
                  >
                    No contracts found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="border-t border-gray-100 dark:border-dark-border bg-gray-50/50 dark:bg-gray-800/30 p-2">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            itemsPerPage={itemsPerPage}
            onItemsPerPageChange={setItemsPerPage}
            totalItems={totalItems}
            loading={contractsLoading}
          />
        </div>
      </div>

      {/* Terminate Modal */}
      {showTerminateModal && selectedContract && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-dark-surface rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            <div className="px-6 py-5 border-b border-gray-200 dark:border-dark-border">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-red-50 dark:bg-red-900/20">
                  <AlertCircle
                    className="text-red-600 dark:text-red-400"
                    size={24}
                  />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-text dark:text-dark-text">
                    Terminate Contract
                  </h2>
                  <p className="text-sm text-gray-500 dark:text-dark-muted">
                    This action cannot be undone
                  </p>
                </div>
              </div>
            </div>

            <div className="p-6">
              <div className="mb-4">
                <p className="text-sm text-gray-600 dark:text-dark-muted mb-2">
                  Contract between:
                </p>
                <div className="p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg space-y-1">
                  <p className="text-sm font-medium text-text dark:text-dark-text">
                    Client: {getClient(selectedContract).name || "N/A"}
                  </p>
                  <p className="text-sm font-medium text-text dark:text-dark-text">
                    Caregiver: {selectedContract.caregiver?.name || "N/A"}
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-text dark:text-dark-text">
                  Termination Reason <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={terminationReason}
                  onChange={(e) => setTerminationReason(e.target.value)}
                  placeholder="Please provide a reason for termination..."
                  rows={4}
                  className="w-full px-4 py-3 rounded-lg bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-dark-border outline-none focus:ring-2 focus:ring-red-500/20 text-text dark:text-dark-text placeholder-gray-400 resize-none"
                />
              </div>
            </div>

            <div className="px-6 py-4 bg-gray-50 dark:bg-gray-900/50 flex gap-3">
              <button
                onClick={() => {
                  setShowTerminateModal(false);
                  setSelectedContract(null);
                  setTerminationReason("");
                }}
                className="flex-1 px-4 py-2.5 bg-white dark:bg-dark-surface text-gray-700 dark:text-dark-text rounded-lg font-medium hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors border border-gray-200 dark:border-dark-border"
              >
                Cancel
              </button>
              <button
                onClick={handleTerminate}
                disabled={!terminationReason.trim()}
                className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Terminate Contract
              </button>
            </div>
          </div>
        </div>
      )}

      {showStartDateModal && selectedContract && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-dark-surface rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            <div className="px-6 py-5 border-b border-gray-200 dark:border-dark-border flex items-start justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-50 dark:bg-blue-900/20">
                  <CalendarClock
                    className="text-blue-600 dark:text-blue-400"
                    size={24}
                  />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-text dark:text-dark-text">
                    Adjust Contract Start Date
                  </h2>
                  <p className="text-sm text-gray-500 dark:text-dark-muted">
                    {selectedContract.caregiver?.name || "Caregiver"}
                  </p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowStartDateModal(false);
                  setSelectedContract(null);
                }}
                className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800"
                aria-label="Close"
              >
                <XCircle size={20} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-text dark:text-dark-text mb-2">
                  Correct start date
                </label>
                <input
                  type="date"
                  value={startDateValue}
                  max={formatDateForInput(new Date())}
                  onChange={(event) => setStartDateValue(event.target.value)}
                  className="w-full px-4 py-3 rounded-lg bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-dark-border outline-none focus:ring-2 focus:ring-blue-500/20 text-text dark:text-dark-text"
                />
              </div>

              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
                This recalculates unpaid payment periods from the selected
                date. For safety, a contract with any wage payment or payout
                history cannot be changed.
              </div>
            </div>

            <div className="px-6 py-4 bg-gray-50 dark:bg-gray-900/50 flex gap-3">
              <button
                onClick={() => {
                  setShowStartDateModal(false);
                  setSelectedContract(null);
                }}
                disabled={isUpdatingStartDate}
                className="flex-1 px-4 py-2.5 bg-white dark:bg-dark-surface text-gray-700 dark:text-dark-text rounded-lg font-medium hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors border border-gray-200 dark:border-dark-border disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleStartDateUpdate}
                disabled={!startDateValue || isUpdatingStartDate}
                className="flex-1 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isUpdatingStartDate ? "Updating..." : "Update Date"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContractList;
